/**
 * File contains functions for working with Firebase's Storage API.
 */

import { deleteObject, getDownloadURL, ref, StorageReference, uploadBytes } from 'firebase/storage';

import { storage, STORAGE_URL } from '../Firebase';
import {
  COVER_IMAGE_HEIGHT,
  COVER_IMAGE_WIDTH,
  PROFILE_IMAGE_HEIGHT,
  PROFILE_IMAGE_WIDTH,
} from '../../app-constants/profileAndCoverImagesSize';
import { canvasToBuffer, ImageFormat, loadImageOnCanvas } from '../utility/image/resizeImage';
import { processImage } from '../utility/image/imageProcessor';
import { resizeAndCropToKeepAspect } from '../utility/image/imageProcessingPipelines';
import { GAME_EVENT_IMAGE_HEIGHT, GAME_EVENT_IMAGE_WIDTH } from '../../app-constants/gameEvent';

/**
 * Deletes file from Firebase storage.
 *
 * @param file
 * @return Promise resolved to true if file deleted successfully, or didn't exist.
 */
export const deleteFile = (file: 'string' | StorageReference): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    const reference = typeof file === 'string' ? getStorageReferenceFromUrl(file) : file;

    storageFileExists(reference)
      .then((result) => {
        if (result) {
          deleteObject(reference)
            .then(() => resolve(true))
            .catch((error) => reject(error));
        } else {
          resolve(true);
        }
      })
      .catch((error) => {
        throw new Error(error);
      });
  });
};

/**
 * Prepares url by running cleanupUrl functions and retrieves its storage reference
 * if file exists.
 *
 * @param url Url or path to image.
 */
export const getStorageReferenceFromUrl = (url: string) => {
  const path = cleanupUrl(url);
  return ref(storage, path);
};

/**
 * Removes base STORAGE_URL and any query variables to get clean path
 * that can then be passed to `ref()` to create storage object reference.
 *
 * Example input and output:
 * input: https://storage.googleapis.com/jigsaw-puzzle-universe-web.appspot.com/users/08dGD7B5S2QdHmhWXtBFG2E3DDs2/profile.png?timestamp=1675446113679
 * output: /users/08dGD7B5S2QdHmhWXtBFG2E3DDs2/profile.png
 *
 * @param url Url to clean up.
 * @see STORAGE_URL
 */
export const cleanupUrl = (url: string) => {
  // Remove base path.
  url = url.replace(STORAGE_URL, '');

  // Remove query parameters (everything after first "?" character).
  url = url.replace(/(\?.*)$/, '');

  return url;
};

/**
 * Check if file exists in storage.
 * Since there is no Firebase native API for this, it is resolved by try using
 * getDownloadUrl which returns url on success, and object-not-found on fail.
 *
 * @param url Url or storageRef to check.
 */
export const storageFileExists = (url: string | StorageReference): Promise<boolean> => {
  const reference = typeof url === 'string' ? ref(storage, cleanupUrl(url)) : url;

  return new Promise((resolve, reject) => {
    getDownloadURL(reference)
      .then(() => {
        return resolve(true);
      })
      .catch((error) => {
        if (error.code === 'storage/object-not-found') {
          return resolve(false);
        } else {
          return reject(error);
        }
      });
  });
};

/**
 * Downloads file blob from a given url.
 * Only works with files stored in Firebase Storage.
 *
 * @param url
 */
export const downloadFile = (url: string): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    storageFileExists(url)
      .then((status) => {
        !status && reject("File doesn't exist");
      })
      .catch((error) => reject(error));

    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        resolve(blob);
      })
      .catch((error) => reject(error));
  });
};

/**
 * Upload artist's profile/cover image.
 *
 * Original and cropped image are uploaded to following paths depending on type argument:
 * STORAGE_URL/users/userId/original_profile
 * STORAGE_URL/users/userId/profile
 * STORAGE_URL/users/userId/original_cover
 * STORAGE_URL/users/userId/cover
 *
 * @param artistId Artist for which to update image.
 * @param image Image to upload.
 * @param type Dictates whether profile or cover image should be updated and sets appropriate image crop resolution.
 */
export async function uploadArtistImage(artistId: string, image: File, type: 'profile' | 'cover') {
  // 1. Setup.
  const imageWidth = type === 'profile' ? PROFILE_IMAGE_WIDTH : COVER_IMAGE_WIDTH;
  const imageHeight = type === 'profile' ? PROFILE_IMAGE_HEIGHT : COVER_IMAGE_HEIGHT;

  const originalImagePath =
    type === 'profile' ? `users/${artistId}/original_profile` : `users/${artistId}/original_cover`;
  const croppedImagePath =
    type === 'profile' ? `users/${artistId}/profile` : `users/${artistId}/cover`;

  const originalImageReference = ref(storage, originalImagePath);
  const croppedImageReference = ref(storage, croppedImagePath);

  const originalImage = image;
  const canvas = await loadImageOnCanvas(URL.createObjectURL(image));
  const croppedImage = await canvasToBuffer(
    canvas.width > imageWidth || canvas.height > imageHeight
      ? await processImage(resizeAndCropToKeepAspect, canvas, imageWidth, imageHeight)
      : canvas,
    ImageFormat.JPEG,
    0.9,
  );

  // 2. Upload.
  const uploadResults = await Promise.all([
    uploadBytes(croppedImageReference, croppedImage, {
      contentType: ImageFormat.JPEG,
      customMetadata: { owner: artistId },
    }),
    uploadBytes(originalImageReference, originalImage, {
      contentType: ImageFormat.JPEG,
      customMetadata: { owner: artistId },
    }),
  ]).catch((error) => {
    console.log(error);
    throw new Error("Couldn't upload images.");
  });

  return uploadResults;
}

const uploadImageToStorage = async (
  path: string,
  file: File,
  dimensions?: { width: number; height: number },
) => {
  const format = file.type as ImageFormat;

  const canvas = await loadImageOnCanvas(URL.createObjectURL(file));
  const resizedImage =
    dimensions && (canvas.width > dimensions.width || canvas.height > dimensions.height)
      ? await processImage(resizeAndCropToKeepAspect, canvas, dimensions.width, dimensions.height)
      : canvas;

  const croppedImage = await canvasToBuffer(resizedImage, format, 0.9);

  const imageReference = ref(storage, path);
  return uploadBytes(imageReference, croppedImage, {
    contentType: format,
  });
};

export async function uploadGameEventImage(gameEventId: string, image: File) {
  return uploadImageToStorage(`gameEvents/${gameEventId}/image`, image, {
    width: GAME_EVENT_IMAGE_WIDTH,
    height: GAME_EVENT_IMAGE_HEIGHT,
  });
}

export async function uploadColoringGridImage(coloringGridId: string, image: File) {
  return uploadBytes(ref(storage, `coloringGrids/${coloringGridId}/original`), image, {
    contentType: image.type as ImageFormat,
  });
}

export async function uploadThemeImage(
  themeId: string,
  image: File,
  kind: 'background' | 'board' | 'preview',
) {
  return uploadBytes(ref(storage, `gameplayThemes/${themeId}/${kind}`), image, {
    contentType: image.type as ImageFormat,
  });
}
